// ----------------------------------------------------------------------------
// Section: Featured Collection — Grid
// ----------------------------------------------------------------------------
//
// Handles tab switching between collection panels. Products use a CSS grid
// (see `_featured-collection.scss`); no carousel library.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import 'snippets/card/card';
import 'snippets/icon/icon';
import './_featured-collection.scss';

// -------------------------------------
// Constants
// -------------------------------------

var SECTION_SELECTOR = '.featured-collection--grid';
var TAB_BTN_SELECTOR = '.featured-collection__tab-btn';
var PANEL_SELECTOR = '.featured-collection__panel';
var HIDDEN_CLASS = 'is-hidden';

/** Abort prior tab listeners when the same section root is re-inited (theme editor). */
var sectionListenersAbort = new WeakMap();

// -------------------------------------
// Tab switching
// -------------------------------------

/**
 * Activates a tab button and its corresponding panel, deactivates all others.
 *
 * @param {Element} clickedBtn - The tab button that was activated
 * @param {NodeList} allBtns - All tab buttons in this section
 * @param {NodeList} allPanels - All panels in this section
 */
function activateTab(clickedBtn, allBtns, allPanels) {
  if (clickedBtn.getAttribute('aria-selected') === 'true') return;
  var targetPanelId = clickedBtn.getAttribute('aria-controls');
  var targetPanel = document.getElementById(targetPanelId);
  if (!targetPanel) return;
  allBtns.forEach(function (btn) {
    btn.setAttribute('aria-selected', 'false');
    btn.setAttribute('tabindex', '-1');
  });
  allPanels.forEach(function (panel) {
    if (!panel.classList.contains(HIDDEN_CLASS)) {
      panel.classList.add(HIDDEN_CLASS);
    }
  });
  clickedBtn.setAttribute('aria-selected', 'true');
  clickedBtn.setAttribute('tabindex', '0');
  targetPanel.classList.remove(HIDDEN_CLASS);
}

// -------------------------------------
// Keyboard navigation
// -------------------------------------

/**
 * Handles ArrowLeft/Right/Home/End keyboard navigation within a tab list.
 */
function handleKeydown(event, allBtns, allPanels) {
  var key = event.key;
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
  event.preventDefault();
  var current = event.target;
  var btnArray = Array.from(allBtns);
  var currentIndex = btnArray.indexOf(current);
  var length = btnArray.length;
  var newIndex;
  switch (key) {
    case 'ArrowLeft':
      newIndex = (currentIndex - 1 + length) % length;
      break;
    case 'ArrowRight':
      newIndex = (currentIndex + 1) % length;
      break;
    case 'Home':
      newIndex = 0;
      break;
    case 'End':
      newIndex = length - 1;
      break;
    default:
      return;
  }
  btnArray[newIndex].focus();
  activateTab(btnArray[newIndex], allBtns, allPanels);
}

// -------------------------------------
// Section init
// -------------------------------------

/**
 * Initializes tab switching for a single Featured Collection section.
 *
 * @param {Element} section - The `.featured-collection--grid` root element
 */
function initSection(section) {
  var allBtns = section.querySelectorAll(TAB_BTN_SELECTOR);
  var allPanels = section.querySelectorAll(PANEL_SELECTOR);
  if (!allPanels.length) return;
  var prevAbort = sectionListenersAbort.get(section);
  if (prevAbort) {
    prevAbort.abort();
  }
  var abort = new AbortController();
  sectionListenersAbort.set(section, abort);
  var listenOpts = {
    signal: abort.signal
  };
  if (allBtns.length > 1) {
    allBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        activateTab(btn, allBtns, allPanels);
      }, listenOpts);
      btn.addEventListener('keydown', function (event) {
        handleKeydown(event, allBtns, allPanels);
      }, listenOpts);
    });
  }
}

// -------------------------------------
// Mount
// -------------------------------------

function mountSections() {
  document.querySelectorAll(SECTION_SELECTOR).forEach(function (section) {
    initSection(section);
  });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountSections);
} else {
  mountSections();
}
document.addEventListener('shopify:section:load', function (event) {
  var section = event.target.querySelector(SECTION_SELECTOR);
  if (section) {
    initSection(section);
  }
});